import { User, BlockedUser, UserConnection, UserStatus, UserType } from "~/models/user"
import { defineStore } from "pinia"
import { Address } from "~/models/address"
import { ApiResponse } from "~/types/api"
import { DirectMessage } from "~/models/direct-message"

export type UserStatusType = {
	name: string
	value: string
	borderClass: string
	bgClass: string
	txtClass: string
}

export type UserAuthMode = ReturnType<typeof useUserStore>["authModes"][number]

export const useUserStore = defineStore("user", () => {
	const tokenCookie = useCookie("token", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const refreshTokenCookie = useCookie("refresh_token", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const router = useRouter()
	const routeUrl = useRouteUrl()
	const { $i18n } = useNuxtApp()

	const state = {
		user: ref<User | null>(null),
		addresses: ref<Address[] | null>(null),
		token: ref(tokenCookie.value),
		refreshToken: ref(refreshTokenCookie.value),
		time: ref(0),
		elapsedTime: ref(0),
		types: ref([
			{
				name: $i18n.t("user"),
				value: "user"
			},
			{
				name: $i18n.t("blogger"),
				value: "blogger"
			},
			{
				name: $i18n.t("support"),
				value: "support"
			},
			{
				name: $i18n.t("developer"),
				value: "developer"
			}
		] as UserStatusType[]),
		statuses: ref([
			{
				name: $i18n.t("enable"),
				value: "enable"
			},
			{
				name: $i18n.t("disable"),
				value: "disable"
			},
			{
				name: $i18n.t("block"),
				value: "block"
			}
		] as UserStatusType[]),
		authModes: ref([
			{
				id: "email",
				title: $i18n.t("email")
			},
			{
				id: "mobile",
				title: $i18n.t("mobile")
			}
		] as const)
	}

	const setToken = (token: string | null) => {
		state.token.value = token
		tokenCookie.value = token
	}

	const setRefreshToken = (refreshToken: string | null) => {
		state.refreshToken.value = refreshToken
		refreshTokenCookie.value = refreshToken
	}

	const login = async (formData: LoginRequest) => {
		const r = await $fetch<LoginResponse>(Login.url(), {
			...useFetchConfig(),
			method: Login.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token!!)
			setRefreshToken(r.data.refresh_token!!)
		}
		return r.data
	}

	const check = async (formData: CheckRequest) => {
		const r = await $fetch<CheckResponse>(Check.url(), {
			...useFetchConfig(),
			method: Check.method,
			body: formData
		})
		return r.data
	}

	const register = async (formData: RegisterRequest) => {
		const r = await $fetch<RegisterResponse>(Register.url(), {
			...useFetchConfig(),
			method: Register.method,
			body: formData
		})
		return r.data
	}

	const adminLogin = async (formData: AdminLoginRequest) => {
		const r = await $fetch<AdminLoginResponse>(AdminLogin.url(), {
			...useFetchConfig(),
			method: AdminLogin.method,
			body: formData
		})
		if (r.data.status === "enable") {
			state.user.value = r.data
			setToken(r.data.token!!)
			setRefreshToken(r.data.refresh_token!!)
		}
		return r.data
	}

	const editProfile = async (formData: EditProfileRequest) => {
		const r = await $fetch<EditProfileResponse>(EditProfile.url(), {
			...useFetchConfig(),
			method: EditProfile.method,
			body: formData
		})
		state.user.value = r.data
		return r.data
	}

	const verifyEmail = async (formData: VerifyEmailRequest) => {
		const r = await $fetch<VerifyEmailResponse>(VerifyEmail.url(), {
			...useFetchConfig(),
			method: VerifyEmail.method,
			body: formData
		})
		return r.data
	}

	const verifyMobile = async (formData: VerifyMobileRequest) => {
		const r = await $fetch<VerifyMobileResponse>(VerifyMobile.url(), {
			...useFetchConfig(),
			method: VerifyMobile.method,
			body: formData
		})
		return r.data
	}

	const verifyForget = async (formData: VerifyForgetRequest) => {
		const r = await $fetch<VerifyForgetResponse>(VerifyForget.url(), {
			...useFetchConfig(),
			method: VerifyForget.method,
			body: formData
		})
		return r.data
	}

	const requestForget = async (formData: ForgetPasswordRequest) => {
		const r = await $fetch<ForgetPasswordResponse>(ForgetPassword.url(), {
			...useFetchConfig(),
			method: ForgetPassword.method,
			body: formData
		})
		return r.data
	}

	const verifyCode = async (formData: VerifyCodeRequest) => {
		const r = await $fetch<VerifyCodeResponse>(VerifyCode.url(), {
			...useFetchConfig(),
			method: VerifyCode.method,
			body: formData
		})
		return r.data
	}

	const changePassword = async (formData: ChangePasswordRequest) => {
		const r = await $fetch<ChangePasswordResponse>(ChangePassword.url(), {
			...useFetchConfig(),
			method: ChangePassword.method,
			body: formData
		})
		return r.data
	}

	const logout = async () => {
		const businessStore = useBusinessStore()
		state.user.value = null
		setToken(null)
		setRefreshToken(null)
		businessStore.businesses = null
		businessStore.business = null
		await router.push(routeUrl.HomeUrl())
	}

	const adminLogout = async () => {
		state.user.value = null
		setToken(null)
		setRefreshToken(null)
		router.push(routeUrl.AdminLoginUrl())
	}

	const searchUsers = async (formData: SearchUsersRequest) => {
		const r = await $fetch<SearchUsersResponse>(SearchUsers.url(), {
			...useFetchConfig(),
			method: SearchUsers.method,
			query: formData
		})
		return r.data
	}

	const getUser = async (userId: number, params?: GetUserRequest) => {
		const r = await $fetch<GetUserResponse>(GetUser.url(userId), {
			...useFetchConfig(),
			method: SearchUsers.method,
			params
		})
		return r.data
	}

	const createUser = async (formData: CreateUserRequest) => {
		const r = await $fetch<CreateUserResponse>(CreateUser.url(), {
			...useFetchConfig(),
			method: CreateUser.method,
			body: formData
		})
		return r.data
	}

	const editUser = async (formData: EditUserRequest) => {
		const r = await $fetch<EditUserResponse>(EditUser.url(), {
			...useFetchConfig(),
			method: EditUser.method,
			body: formData
		})
		return r.data
	}

	const deleteUser = async (id: number) => {
		const r = await $fetch<DeleteUserResponse>(DeleteUser.url(id), {
			...useFetchConfig(),
			method: DeleteUser.method
		})
		return r.data
	}

	const changeUserStatus = async (formData: ChangeUserStatusRequest) => {
		const r = await $fetch<ChangeUserStatusResponse>(ChangeUserStatus.url(), {
			...useFetchConfig(),
			method: ChangeUserStatus.method,
			body: formData
		})
		return r.data
	}

	const changeUserType = async (formData: ChangeUserTypeRequest) => {
		const r = await $fetch<ChangeUserTypeResponse>(ChangeUserType.url(), {
			...useFetchConfig(),
			method: ChangeUserType.method,
			body: formData
		})
		return r.data
	}

	const getCaptcha = async () => {
		const r = await $fetch<GetCaptchaResponse>(GetCaptcha.url(), {
			...useFetchConfig(),
			method: GetCaptcha.method
		})
		return r.data
	}

	const sendDirectMessage = async (formData: SendDirectMessageRequest) => {
		const r = await $fetch<SendDirectMessageResponse>(SendDirectMessage.url(), {
			...useFetchConfig(),
			method: SendDirectMessage.method,
			body: formData
		})
		return r.data
	}

	const searchDirectMessages = async (query: SearchDirectMessagesRequest) => {
		const r = await $fetch<SearchDirectMessagesResponse>(SearchDirectMessages.url(), {
			...useFetchConfig(),
			method: SearchDirectMessages.method,
			query
		})
		return r.data
	}

	const connect = async (formData: UserConnectRequest) => {
		const r = await $fetch<UserConnectResponse>(UserConnect.url(), {
			...useFetchConfig(),
			method: UserConnect.method,
			body: formData
		})
		return r.data
	}

	const disconnect = async (formData: UserDisconnectRequest) => {
		const r = await $fetch<UserDisconnectResponse>(UserDisconnect.url(), {
			...useFetchConfig(),
			method: UserDisconnect.method,
			body: formData
		})
		return r.data
	}

	const block = async (formData: UserBlockRequest) => {
		const r = await $fetch<UserBlockResponse>(UserBlock.url(), {
			...useFetchConfig(),
			method: UserBlock.method,
			body: formData
		})
		return r.data
	}

	const unblock = async (formData: UserUnblockRequest) => {
		const r = await $fetch<UserUnblockResponse>(UserUnblock.url(), {
			...useFetchConfig(),
			method: UserUnblock.method,
			body: formData
		})
		return r.data
	}

	return {
		...state,
		getCaptcha,
		setToken,
		setRefreshToken,
		login,
		check,
		register,
		adminLogin,
		editProfile,
		logout,
		verifyForget,
		adminLogout,
		verifyEmail,
		verifyMobile,
		requestForget,
		verifyCode,
		changePassword,
		searchUsers,
		createUser,
		editUser,
		deleteUser,
		changeUserStatus,
		changeUserType,
		sendDirectMessage,
		searchDirectMessages,
		getUser,
		connect,
		block,
		unblock,
		disconnect
	}
})

// auth :: login
export interface LoginRequest {
	email?: string
	mobile?: string
	password: string
	captcha_key: string
	captcha: string
	locale?: string
}
export interface LoginResponse extends ApiResponse {
	data: User
}
const Login = {
	method: "POST" as "POST",
	url: () => "auth/login"
}

// auth :: admin check
export interface CheckRequest {
	email?: string
	mobile?: string
}
export interface CheckResponse extends ApiResponse {
	data: {
		status: UserStatus | null
	}
}
const Check = {
	method: "POST" as "POST",
	url: () => "auth/check"
}

// auth :: admin login
export interface AdminLoginRequest {
	email: string
	password: string
	captcha_key: string
	captcha: string
}
export interface AdminLoginResponse extends ApiResponse {
	data: User
}
const AdminLogin = {
	method: "POST" as "POST",
	url: () => "auth/admin-login"
}

// auth :: register
export interface RegisterRequest {
	email?: string
	mobile?: string
	password: string
	firstname: string
	lastname: string
	longitude: number
	latitude: number
	captcha_key: string
	captcha: string
}
export interface RegisterResponse extends ApiResponse {
	data: User
}
const Register = {
	method: "POST" as "POST",
	url: () => "auth/register"
}

// auth :: get captcha
export interface GetCaptchaRequest {}
export interface GetCaptchaResponse extends ApiResponse {
	data: {
		text?: string
		image: string
		key: string
	}
}
const GetCaptcha = {
	method: "GET" as "GET",
	url: () => "auth/captcha"
}

// auth :: forget password
export interface ForgetPasswordRequest {
	email?: string
	mobile?: string
	captcha_key: string
	captcha: string
}
export interface ForgetPasswordResponse extends ApiResponse {
	data: {
		code?: string
	}
}
const ForgetPassword = {
	method: "POST" as "POST",
	url: () => "auth/forget-password"
}

// auth :: verify email
export interface VerifyEmailRequest {
	email: string
	code: string
}
export interface VerifyEmailResponse extends ApiResponse {
	data: null
}
const VerifyEmail = {
	method: "POST" as "POST",
	url: () => "auth/verify-email"
}

// auth :: verify mobile
export interface VerifyMobileRequest {
	mobile: string
	code: string
}
export interface VerifyMobileResponse extends ApiResponse {
	data: null
}
const VerifyMobile = {
	method: "POST" as "POST",
	url: () => "auth/verify-mobile"
}

// auth :: verify forget
export interface VerifyForgetRequest {
	email: string
	code: string
}
export interface VerifyForgetResponse extends ApiResponse {
	data: {
		code?: string
	}
}
const VerifyForget = {
	method: "POST" as "POST",
	url: () => "auth/verify-forget"
}

// auth :: verify code
export interface VerifyCodeRequest {
	email?: string
	mobile?: string
	code: string
}
export interface VerifyCodeResponse extends ApiResponse {
	data: {
		code: string
	}
}
const VerifyCode = {
	method: "POST" as "POST",
	url: () => "auth/verify-code"
}

// auth :: change password
export interface ChangePasswordRequest {
	email?: string
	mobile: string
	password: string
	code: string
}
export interface ChangePasswordResponse extends ApiResponse {
	data: null
}
const ChangePassword = {
	method: "POST" as "POST",
	url: () => "auth/change-password"
}

// auth :: refresh token
export interface RefreshTokenRequest {
	refresh_token: string
}
export interface RefreshTokenResponse extends ApiResponse {
	data: {
		token: string
	}
}
const RefreshToken = {
	method: "POST" as "POST",
	url: () => "auth/refresh-token"
}

// auth :: logout
const Logout = {
	method: "GET" as "GET",
	url: () => "auth/logout"
}

// user :: edit profile
export interface EditProfileRequest {
	firstname?: string
	lastname?: string
	bio?: string
	longitude?: number
	latitude?: number
	locale?: string
	birthday?: string
	mobile?: string | null
	old_password?: string
	new_password?: string
	image_id?: number | null
	billing_address_id?: number | null
	shipping_address_id?: number | null
	interests?: number[] | null
	waiting_for_removal?: boolean
}
export interface EditProfileResponse extends ApiResponse {
	data: User
}
const EditProfile = {
	method: "POST" as "POST",
	url: () => "users/profile"
}

// user :: get profile
export interface GetProfileRequest {}
export interface GetProfileResponse extends ApiResponse {
	data: User
}
const GetProfile = {
	method: "GET" as "GET",
	url: () => "users/profile"
}

// user :: search users
export interface SearchUsersRequest {
	search?: string
	type?: UserType
	page?: number
	limit?: number
	order_by?: "created" | "updated" | "direct-message-created" | "connection-created" | "block-created"
	direction?: "asc" | "desc"
	direct_message_business_id?: number
	has_direct_message?: boolean
	is_connected?: boolean
	is_blocked?: boolean
	moderation_statuses?: string
	is_moderated?: boolean
}
export interface SearchUsersResponse extends ApiResponse {
	data: {
		users: User[]
		total: number
	}
}
const SearchUsers = {
	method: "GET" as "GET",
	url: () => "users/search"
}

// user :: create user
export interface CreateUserRequest {
	firstname?: string
	lastname?: string
	mobile?: string
	email?: string
	status?: UserStatus
	type?: UserType
	password?: string
}
export interface CreateUserResponse extends ApiResponse {
	data: User
}
const CreateUser = {
	method: "POST" as "POST",
	url: () => "users/create"
}

// user :: edit user
export interface EditUserRequest {
	id: number
	firstname?: string
	lastname?: string
	birthday?: string
	email?: string
	mobile?: string
	status?: string
	type?: string
	moderation_status?: string
	moderation_reject_reason?: string
	password?: string
}
export interface EditUserResponse extends ApiResponse {
	data: User
}
const EditUser = {
	method: "POST" as "POST",
	url: () => "users/edit"
}

// user :: delete user
export interface DeleteUserRequest {}
export interface DeleteUserResponse extends ApiResponse {}
const DeleteUser = {
	method: "POST" as "POST",
	url: (id: number) => `users/delete/${id}`
}

// user :: change user status
export interface ChangeUserStatusRequest {
	user_id: number
	status: UserStatus
}
export interface ChangeUserStatusResponse extends ApiResponse {
	data: User
}
const ChangeUserStatus = {
	method: "POST" as "POST",
	url: () => `users/status`
}

// user :: change type type
export interface ChangeUserTypeRequest {
	user_id: number
	type: "user" | "developer"
}
export interface ChangeUserTypeResponse extends ApiResponse {
	data: User
}
const ChangeUserType = {
	method: "POST" as "POST",
	url: () => `users/type`
}

// user :: send direct message
export interface SendDirectMessageRequest {
	user_id?: number
	business_id?: number
	message: string
}
export interface SendDirectMessageResponse extends ApiResponse {
	data: DirectMessage
}
const SendDirectMessage = {
	method: "POST" as "POST",
	url: () => `users/send-direct-message`
}

// user :: search direct messages
export interface SearchDirectMessagesRequest {
	business_id?: number
	user_id?: number
	page?: number
	limit?: number
	order_by?: "created" | "updated"
	direction?: "asc" | "desc"
}
export interface SearchDirectMessagesResponse extends ApiResponse {
	data: {
		direct_messages: DirectMessage[]
		total: number
	}
}
const SearchDirectMessages = {
	method: "GET" as "GET",
	url: () => `users/search-direct-messages`
}

// user :: get user
export interface GetUserRequest {
	moderation_statuses?: string
	is_moderated?: boolean
}
export interface GetUserResponse extends ApiResponse {
	data: User
}
const GetUser = {
	method: "GET" as "GET",
	url: (id: number) => `users/${id}`
}

// user :: connect
export interface UserConnectRequest {
	user_id: number
}
export interface UserConnectResponse extends ApiResponse {
	data: UserConnection
}
const UserConnect = {
	method: "POST" as "POST",
	url: () => `users/connect`
}

// user :: disconnect
export interface UserDisconnectRequest {
	user_id: number
}
export interface UserDisconnectResponse extends ApiResponse {
	data: null
}
const UserDisconnect = {
	method: "POST" as "POST",
	url: () => `users/disconnect`
}

// user :: block
export interface UserBlockRequest {
	user_id: number
}
export interface UserBlockResponse extends ApiResponse {
	data: BlockedUser
}
const UserBlock = {
	method: "POST" as "POST",
	url: () => `users/block`
}

// user :: unblock
export interface UserUnblockRequest {
	user_id: number
}
export interface UserUnblockResponse extends ApiResponse {
	data: null
}
const UserUnblock = {
	method: "POST" as "POST",
	url: () => `users/unblock`
}

export const useUserApiUrl = () => ({
	Login,
	Check,
	AdminLogin,
	Register,
	GetCaptcha,
	ForgetPassword,
	VerifyEmail,
	VerifyMobile,
	VerifyCode,
	ChangePassword,
	RefreshToken,
	Logout,
	EditProfile,
	GetProfile,
	SearchUsers,
	CreateUser,
	EditUser,
	DeleteUser,
	ChangeUserStatus,
	ChangeUserType,
	SendDirectMessage,
	SearchDirectMessages,
	GetUser,
	UserConnect,
	UserDisconnect
})
